import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { parseCSV, parseTabDelimited } from './services/csvParser';
import { geocodeAddress } from './services/geocoding';
import { generateRoutePDF } from './services/pdfService';
import TableControls from './components/TableControls';
import AddressTable from './components/AddressTable';
import MapControls from './components/MapControls';
import MapComponent from './components/MapComponent';
import TabDelimitedModal from './components/TabDelimitedModal';
import { Address, StartingPoint, RouteData } from './types';
import { Info, Upload, MapPin, Eye, EyeOff, Plus, Trash2, FileUp, ClipboardList, RotateCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

function App() {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const [startingPoint, setStartingPoint] = useState<StartingPoint>({
    address: '6691 Frontier Dr, Springfield, VA 22150',
    isValid: false
  });
  const [isGeocodingStart, setIsGeocodingStart] = useState(false);
  const [isCreatingRoute, setIsCreatingRoute] = useState(false);
  const [hasRoute, setHasRoute] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<google.maps.DirectionsResult | null>(null);
  const [mapKey, setMapKey] = useState(0); // Force map re-render when needed
  const [tableWidth, setTableWidth] = useState(40); // percent
  const isResizing = useRef(false);
  const [showInfo, setShowInfo] = useState(false);

  // Memoized computed values to prevent unnecessary re-renders
  const selectedAddresses = useMemo(() => 
    addresses.filter(addr => addr.isSelected), 
    [addresses]
  );

  const hasAddresses = useMemo(() => 
    addresses.length > 0, 
    [addresses.length]
  );

  const hasSelectedPoints = useMemo(() => 
    selectedAddresses.length > 0, 
    [selectedAddresses.length]
  );

  // Detect duplicates with useCallback to prevent recreation
  const detectDuplicates = useCallback((addressList: Address[]): Address[] => {
    const addressMap = new Map<string, Address[]>();
    
    addressList.forEach(addr => {
      const key = addr.address.toLowerCase().trim();
      if (!addressMap.has(key)) {
        addressMap.set(key, []);
      }
      addressMap.get(key)!.push(addr);
    });

    return addressList.map(addr => ({
      ...addr,
      isDuplicate: addressMap.get(addr.address.toLowerCase().trim())!.length > 1
    }));
  }, []);

  // Stable event handlers
  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const parsedAddresses = await parseCSV(file);
      setAddresses(prev => {
        const combined = [...prev, ...parsedAddresses];
        return detectDuplicates(combined);
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert('Error parsing CSV file. Please check the format.');
    }
  }, [detectDuplicates]);

  const handleTabDelimitedSubmit = useCallback((text: string) => {
    try {
      const parsedAddresses = parseTabDelimited(text);
      setAddresses(prev => {
        const combined = [...prev, ...parsedAddresses];
        return detectDuplicates(combined);
      });
    } catch (error) {
      console.error('Error parsing tab-delimited data:', error);
      alert('Error parsing tab-delimited data.');
    }
  }, [detectDuplicates]);

  const handleClearTable = useCallback(() => {
    if (window.confirm('Are you sure you want to clear all addresses?')) {
      setAddresses([]);
      setCurrentRoute(null);
      setHasRoute(false);
    }
  }, []);

  const handleGeocodeAll = useCallback(async () => {
    const addressesToGeocode = addresses.filter(addr => 
      addr.address && (!addr.latitude || !addr.longitude)
    );
    
    if (addressesToGeocode.length === 0) {
      alert('No addresses to geocode.');
      return;
    }

    setIsGeocoding(true);
    
    try {
      for (const addr of addressesToGeocode) {
        const result = await geocodeAddress(addr.address);
        if (result) {
          // Update the address immediately after geocoding
          setAddresses(prev => prev.map(a => 
            a.id === addr.id 
              ? { ...a, latitude: result.lat, longitude: result.lng }
              : a
          ));
        }
        // Rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error('Error geocoding addresses:', error);
      alert('Error geocoding addresses. Please try again.');
    } finally {
      setIsGeocoding(false);
    }
  }, [addresses]);

  const handleUpdateAddress = useCallback((id: string, updates: Partial<Address>) => {
    setAddresses(prev => prev.map(addr => 
      addr.id === id 
        ? { ...addr, ...updates, isHighlighted: false }
        : { ...addr, isHighlighted: false }
    ));
  }, []);

  const handleDeleteAddress = useCallback((id: string) => {
    setAddresses(prev => prev.filter(addr => addr.id !== id));
  }, []);

  const handleGeocodeAddress = useCallback(async (id: string) => {
    const address = addresses.find(addr => addr.id === id);
    if (!address?.address) return;

    setIsGeocoding(true);
    
    try {
      const result = await geocodeAddress(address.address);
      if (result) {
        // Update the address immediately after geocoding
        setAddresses(prev => prev.map(addr => 
          addr.id === id 
            ? { ...addr, latitude: result.lat, longitude: result.lng }
            : addr
        ));
      } else {
        alert('Could not geocode this address.');
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      alert('Error geocoding address.');
    } finally {
      setIsGeocoding(false);
    }
  }, [addresses]);

  const handleAddressClick = useCallback((address: Address) => {
    setAddresses(prev => prev.map(addr => ({
      ...addr,
      isHighlighted: addr.id === address.id
    })));

    // Scroll to address in table
    setTimeout(() => {
      const element = document.getElementById(`address-row-${address.id}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const handlePolygonComplete = useCallback((selectedAddresses: Address[]) => {
    setAddresses(prev => prev.map(addr => ({
      ...addr,
      isSelected: selectedAddresses.some(selected => selected.id === addr.id)
    })));
  }, []);

  // Starting point handlers
  const handleStartingAddressChange = useCallback((address: string) => {
    setStartingPoint(prev => ({
      ...prev,
      address,
      isValid: false,
      latitude: undefined,
      longitude: undefined
    }));
  }, []);

  const handleGeocodeStartingPoint = useCallback(async () => {
    if (!startingPoint.address) return;

    setIsGeocodingStart(true);
    
    try {
      const result = await geocodeAddress(startingPoint.address);
      if (result) {
        setStartingPoint(prev => ({
          ...prev,
          latitude: result.lat,
          longitude: result.lng,
          isValid: true
        }));
      } else {
        alert('Could not geocode starting point address.');
        setStartingPoint(prev => ({ ...prev, isValid: false }));
      }
    } catch (error) {
      console.error('Error geocoding starting point:', error);
      alert('Error geocoding starting point.');
      setStartingPoint(prev => ({ ...prev, isValid: false }));
    } finally {
      setIsGeocodingStart(false);
    }
  }, [startingPoint.address]);

  const handleClearStartingPoint = useCallback(() => {
    setStartingPoint({
      address: '',
      isValid: false,
      latitude: undefined,
      longitude: undefined
    });
  }, []);

  // Route handlers
  const handleCreateRoute = useCallback(() => {
    if (!startingPoint.isValid) {
      alert('Starting point is invalid or not geocoded.');
      return;
    }
    
    if (selectedAddresses.length === 0) {
      alert('Please select addresses by drawing a polygon on the map.');
      return;
    }

    // Check waypoint limit before creating route
    if (selectedAddresses.length > 25) {
      alert(`Too many addresses selected (${selectedAddresses.length}). Google Maps allows a maximum of 25 waypoints per route. Please reduce the number of selected addresses or split into multiple routes.`);
      return;
    }

    setIsCreatingRoute(true);
  }, [startingPoint.isValid, selectedAddresses.length]);

  const handleRouteCreated = useCallback((route: google.maps.DirectionsResult) => {
    setAddresses(prev => prev.map(addr => ({
      ...addr,
      isOnRoute: addr.isSelected
    })));
    
    setCurrentRoute(route);
    setHasRoute(true);
    setIsCreatingRoute(false);
  }, []);

  const handleClearRoute = useCallback(() => {
    setAddresses(prev => prev.map(addr => ({ ...addr, isOnRoute: false })));
    setCurrentRoute(null);
    setHasRoute(false);
  }, []);

  const handleClearPoints = useCallback(() => {
    setAddresses(prev => prev.map(addr => ({ 
      ...addr, 
      isSelected: false, 
      isOnRoute: false 
    })));
    setCurrentRoute(null);
    setHasRoute(false);
    setMapKey(prev => prev + 1); // Force map refresh
  }, []);

  const handleDownloadRoute = useCallback(() => {
    if (!currentRoute || !startingPoint.isValid) {
      alert('No route available to download.');
      return;
    }

    const route = currentRoute.routes[0];
    
    const routeData: RouteData = {
      startingPoint,
      selectedAddresses,
      route: currentRoute,
      totalDistance: route.legs.reduce((total, leg) => total + (leg.distance?.value || 0), 0) / 1000 + ' km',
      totalDuration: Math.round(route.legs.reduce((total, leg) => total + (leg.duration?.value || 0), 0) / 60) + ' minutes'
    };

    try {
      generateRoutePDF(routeData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  }, [currentRoute, startingPoint, selectedAddresses]);

  // Auto-detect coordinates visibility
  useEffect(() => {
    const hasCoordinates = addresses.some(addr => addr.latitude && addr.longitude);
    setShowCoordinates(hasCoordinates);
  }, [addresses]);

  // Add useEffect to geocode starting address on mount
  useEffect(() => {
    async function geocodeStart() {
      if (startingPoint.address && (!startingPoint.latitude || !startingPoint.longitude)) {
        const result = await geocodeAddress(startingPoint.address);
        if (result) {
          setStartingPoint(prev => ({
            ...prev,
            latitude: result.lat,
            longitude: result.lng,
            isValid: true
          }));
        }
      }
    }
    geocodeStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle mouse events for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const totalWidth = window.innerWidth;
      const newTableWidth = Math.max(20, Math.min(80, (e.clientX / totalWidth) * 100));
      setTableWidth(newTableWidth);
    };
    const handleMouseUp = () => {
      isResizing.current = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button
              className="mr-3 p-2 rounded-full hover:bg-blue-100 text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={() => setShowInfo(true)}
              title="About this app"
            >
              <Info size={22} />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Address Mapping & Route Planner
            </h1>
          </div>
        </div>
      </header>

      {/* Info Modal */}
      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-start justify-start bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 mt-12 ml-12 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl font-bold focus:outline-none"
              onClick={() => setShowInfo(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-lg font-bold mb-2 text-blue-700 flex items-center">
              <Info size={20} className="mr-2" />
              About This App
            </h2>
            <div className="text-sm text-gray-700 space-y-3">
              <p>
                <strong>Address Mapping & Route Planner</strong> helps you visualize, geocode, and optimize routes for multiple addresses on a map. You can import addresses, geocode them, select stops, and generate an optimized route and downloadable PDF.
              </p>
              <p>
                <strong>Workflow:</strong>
                <ol className="list-decimal list-inside ml-4 mt-1 space-y-1">
                  <li>Import addresses via CSV or tab-delimited text.</li>
                  <li>Geocode all addresses (find their coordinates).</li>
                  <li>Draw a polygon on the map to select stops for your route.</li>
                  <li>Click 'Create Route' to optimize the order of stops.</li>
                  <li>Download the route as a PDF or clear/reset as needed.</li>
                </ol>
              </p>
              <p>
                <strong>Icon Meanings & Buttons:</strong>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-2">
                  <li className="flex items-center"><Info size={16} className="mr-2 text-blue-600" /> <span>Info: Show this help window.</span></li>
                  <li className="flex items-center"><Upload size={16} className="mr-2 text-blue-600" /> <span>Upload: Import addresses from CSV.</span></li>
                  <li className="flex items-center"><Plus size={16} className="mr-2 text-green-600" /> <span>Add: Add tab-delimited data.</span></li>
                  <li className="flex items-center"><MapPin size={16} className="mr-2 text-purple-600" /> <span>Geocode: Geocode all addresses.</span></li>
                  <li className="flex items-center"><Eye size={16} className="mr-2 text-indigo-600" /> <span>Show Coordinates: Show latitude/longitude columns.</span></li>
                  <li className="flex items-center"><EyeOff size={16} className="mr-2 text-indigo-600" /> <span>Hide Coordinates: Hide latitude/longitude columns.</span></li>
                  <li className="flex items-center"><Trash2 size={16} className="mr-2 text-red-600" /> <span>Delete: Remove an address or clear the table.</span></li>
                  <li className="flex items-center"><RotateCw size={16} className="mr-2 text-blue-600" /> <span>Re-geocode: Geocode a single address again.</span></li>
                  <li className="flex items-center"><CheckCircle size={16} className="mr-2 text-green-600" /> <span>Geocoded: Address successfully geocoded.</span></li>
                  <li className="flex items-center"><AlertCircle size={16} className="mr-2 text-yellow-600" /> <span>Needs Geocoding: Address needs geocoding.</span></li>
                  <li className="flex items-center"><span className="inline-block w-3 h-3 bg-red-600 rounded-full mr-2"></span> <span>Red Dot: Regular address point.</span></li>
                  <li className="flex items-center"><span className="inline-block w-3 h-3 bg-green-600 rounded-full mr-2"></span> <span>Green Dot: Stop on the route.</span></li>
                  <li className="flex items-center"><span className="inline-block w-3 h-3 bg-blue-600 rounded-full mr-2"></span> <span>Blue Dot: Selected address (in polygon).</span></li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Panel - Table (resizable) */}
        <div
          className="flex flex-col bg-white border-r border-gray-200 min-h-0"
          style={{ width: `${tableWidth}%`, minWidth: 200 }}
        >
          <TableControls
            onFileUpload={handleFileUpload}
            onClearTable={handleClearTable}
            onGeocodeAll={handleGeocodeAll}
            onToggleCoordinates={() => setShowCoordinates(!showCoordinates)}
            onAddTabDelimited={() => setIsTabModalOpen(true)}
            showCoordinates={showCoordinates}
            isGeocoding={isGeocoding}
            hasAddresses={hasAddresses}
            addresses={addresses}
          />
          <AddressTable
            addresses={addresses}
            onUpdateAddress={handleUpdateAddress}
            onDeleteAddress={handleDeleteAddress}
            onGeocodeAddress={handleGeocodeAddress}
            showCoordinates={showCoordinates}
            isGeocoding={isGeocoding}
          />
        </div>
        {/* Resizable Divider */}
        <div
          className="w-2 bg-gray-300 cursor-col-resize hover:bg-blue-400 transition-colors"
          style={{ zIndex: 10 }}
          onMouseDown={() => { isResizing.current = true; }}
        />
        {/* Right Panel - Map (resizable) */}
        <div
          className="flex flex-col bg-white min-h-0"
          style={{ width: `calc(${100 - tableWidth}% - 0.5rem)` }}
        >
          <MapControls
            startingAddress={startingPoint.address}
            onStartingAddressChange={handleStartingAddressChange}
            onGeocodeStartingPoint={handleGeocodeStartingPoint}
            onClearStartingPoint={handleClearStartingPoint}
            onCreateRoute={handleCreateRoute}
            onClearRoute={handleClearRoute}
            onClearPoints={handleClearPoints}
            onDownloadRoute={handleDownloadRoute}
            isStartingPointValid={startingPoint.isValid}
            hasSelectedPoints={hasSelectedPoints}
            hasRoute={hasRoute}
            isCreatingRoute={isCreatingRoute}
            isGeocodingStart={isGeocodingStart}
          />
          <div className="flex-1 min-h-0">
            <MapComponent
              key={mapKey}
              addresses={addresses}
              onAddressClick={handleAddressClick}
              onPolygonComplete={handlePolygonComplete}
              startingPoint={startingPoint}
              onRouteCreated={handleRouteCreated}
              shouldCreateRoute={isCreatingRoute}
              onRouteCreationComplete={() => setIsCreatingRoute(false)}
              onClearRoute={() => {}}
              onClearPoints={() => {}}
              shouldClearRoute={false}
              shouldClearPoints={false}
            />
          </div>
        </div>
      </div>

      <TabDelimitedModal
        isOpen={isTabModalOpen}
        onClose={() => setIsTabModalOpen(false)}
        onSubmit={handleTabDelimitedSubmit}
      />
    </div>
  );
}

export default App;